import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Users as UsersIcon, Pencil, Trash2 } from 'lucide-react';
import { userService, type User, type UpdateUserRequest } from '../services/userService';
import { clientService, type Client } from '../services/clientService';
import { useCanManageUsers } from '../hooks/usePermissions';
import PageHeader from '@/components/layout/PageHeader';
import SearchInput from '@/components/shared/SearchInput';
import DataTable, { type ColumnDef } from '@/components/shared/DataTable';
import EmptyState from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

/* ── helpers ──────────────────────────────────────────────────────────────── */
const ROLES = ['SysAdmin', 'DevOps', 'ClientAdmin', 'ProjectMgr', 'Engineer', 'Viewer'] as const;
type Role = typeof ROLES[number];

const roleColor: Record<Role, string> = {
  SysAdmin:    'bg-red-50 text-red-700 border-red-200',
  DevOps:      'bg-orange-50 text-orange-700 border-orange-200',
  ClientAdmin: 'bg-purple-50 text-purple-700 border-purple-200',
  ProjectMgr:  'bg-blue-50 text-blue-700 border-blue-200',
  Engineer:    'bg-green-50 text-green-700 border-green-200',
  Viewer:      'bg-slate-50 text-slate-600 border-slate-200',
};

const RoleBadge = ({ role }: { role: string }) => (
  <Badge variant="outline" className={`text-[11px] ${roleColor[role as Role] ?? 'bg-slate-50 text-slate-600'}`}>{role}</Badge>
);

const fmt = (d: string) => new Date(d).toLocaleDateString();
const isActive = (last: string | null) => {
  if (!last) return false;
  return (Date.now() - new Date(last).getTime()) / 86_400_000 < 90;
};

/* ── extracted form fields (stable identity prevents focus loss) ──────── */
interface UserFormFieldsProps {
  form: { first_name: string; family_name: string; email: string; role: string; client_id: string; username: string };
  setForm: React.Dispatch<React.SetStateAction<{ first_name: string; family_name: string; email: string; role: string; client_id: string; username: string }>>;
  clients: Client[];
}

const UserFormFields = ({ form, setForm, clients }: UserFormFieldsProps) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>{t('pages.users.firstName')} *</Label>
          <Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} required maxLength={100} />
        </div>
        <div className="space-y-1">
          <Label>{t('pages.users.familyName')} *</Label>
          <Input value={form.family_name} onChange={e => setForm(f => ({ ...f, family_name: e.target.value }))} required maxLength={100} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>{t('pages.users.email')}</Label>
          <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} maxLength={200} />
        </div>
        <div className="space-y-1">
          <Label>{t('pages.users.username')}</Label>
          <Input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} minLength={3} maxLength={50} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>{t('pages.users.role')} *</Label>
          <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{t('pages.users.client')} *</Label>
          <Select value={form.client_id || 'none'} onValueChange={v => setForm(f => ({ ...f, client_id: v === 'none' ? '' : v }))}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t('pages.users.createModal.selectClient')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('pages.users.createModal.selectClient')}</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
};

/* ── blank forms ──────────────────────────────────────────────────────────── */
const blankCreate = () => ({ first_name: '', family_name: '', email: '', role: 'Engineer', client_id: '', username: '' });
const blankEdit = () => ({ first_name: '', family_name: '', email: '', role: 'Engineer', client_id: '', username: '' });

/* ── component ────────────────────────────────────────────────────────────── */
export default function Users() {
  const { t } = useTranslation();
  const canManage = useCanManageUsers();

  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState(blankCreate());
  const [editForm, setEditForm] = useState(blankEdit());

  useEffect(() => {
    Promise.all([loadUsers(), loadClients()]);
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true); setError(null);
      setUsers(await userService.getAll());
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pages.users.errorCreate'));
    } finally { setLoading(false); }
  };

  const loadClients = async () => {
    try { setClients(await clientService.getAll()); }
    catch { /* non-blocking */ }
  };

  const clientName = (id?: string) => {
    if (!id) return '—';
    return clients.find(c => c.id === id)?.company_name ?? '—';
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      `${u.first_name} ${u.family_name} ${u.email ?? ''} ${u.username ?? ''} ${u.role}`.toLowerCase().includes(q)
    );
  }, [users, search]);

  /* ── CRUD ───────────────────────────────────────────────────────────────── */
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await userService.create(createForm);
      setCreateOpen(false); setCreateForm(blankCreate());
      await loadUsers();
    } catch (err) { alert(err instanceof Error ? err.message : t('pages.users.errorCreate')); }
    finally { setSaving(false); }
  };

  const openEdit = (u: User) => {
    setEditingUser(u);
    setEditForm({ first_name: u.first_name, family_name: u.family_name, email: u.email ?? '', role: u.role, client_id: u.client_id ?? '', username: u.username ?? '' });
    setEditOpen(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    const update: UpdateUserRequest = {
      first_name: editForm.first_name, family_name: editForm.family_name,
      email: editForm.email || undefined, role: editForm.role,
      client_id: editForm.client_id || undefined, username: editForm.username || undefined,
    };
    try {
      setSaving(true);
      await userService.update(editingUser.id, update);
      setEditOpen(false); setEditingUser(null);
      await loadUsers();
    } catch (err) { alert(err instanceof Error ? err.message : t('pages.users.errorUpdate')); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('pages.users.deleteConfirm'))) return;
    try {
      await userService.delete(id);
      await loadUsers();
    } catch (err) { alert(err instanceof Error ? err.message : t('pages.users.errorDelete')); }
  };

  /* ── table columns ──────────────────────────────────────────────────────── */
  const columns: ColumnDef<User>[] = [
    { key: 'first_name', label: t('pages.users.firstName'), sortable: true,
      render: u => <span className="font-medium text-slate-800">{u.first_name} {u.family_name}</span> },
    { key: 'email', label: t('pages.users.email'), sortable: true,
      render: u => <span className="font-mono text-xs text-slate-600">{u.email ?? '—'}</span> },
    { key: 'role', label: t('pages.users.role'), sortable: true,
      render: u => <RoleBadge role={u.role} /> },
    { key: 'client_id', label: t('pages.users.client'), sortable: false,
      render: u => <span className="text-slate-600">{clientName(u.client_id)}</span> },
    { key: 'last_connected', label: t('pages.users.lastConnected'), sortable: true,
      render: u => (
        <span className={`text-xs ${isActive(u.last_connected) ? 'text-green-600' : 'text-slate-400'}`}>
          {u.last_connected ? fmt(u.last_connected) : t('common.noData')}
        </span>
      ) },
    ...(canManage ? [{
      key: '_actions' as keyof User,
      label: t('common.actions'),
      render: (u: User) => (
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-slate-700" onClick={() => openEdit(u)} title={t('common.edit')}><Pencil size={12} /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-red-600" onClick={() => handleDelete(u.id)} title={t('common.delete')}><Trash2 size={12} /></Button>
        </div>
      ),
    } as ColumnDef<User>] : []),
  ];

  /* ── render ─────────────────────────────────────────────────────────────── */
  if (loading) return (
    <>
      <PageHeader title={t('pages.users.title')} />
      <div className="px-6 py-4 space-y-2">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
      </div>
    </>
  );

  if (error) return (
    <>
      <PageHeader title={t('pages.users.title')} />
      <div className="px-6 py-8 text-sm text-red-600">{error}</div>
    </>
  );

  return (
    <>
      <PageHeader
        title={t('pages.users.title')}
        subtitle={`${users.length} user${users.length !== 1 ? 's' : ''}`}
        actions={canManage ? (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            + {t('pages.users.createUser')}
          </Button>
        ) : undefined}
      />

      <div className="flex items-center gap-2 px-6 pt-4 pb-3">
        <SearchInput value={search} onChange={setSearch} placeholder={`${t('common.search')} users…`} className="w-64" />
      </div>

      <div className="px-6 pb-6">
        {users.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title={t('pages.users.noUsers')}
            action={canManage ? { label: t('pages.users.createUser'), onClick: () => setCreateOpen(true) } : undefined}
          />
        ) : (
          <DataTable
            columns={columns}
            data={filtered}
            emptyIcon={UsersIcon}
            emptyTitle={t('pages.users.noUsers')}
            emptyDescription={t('common.noData')}
          />
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t('pages.users.createModal.title')}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 pt-1">
            <UserFormFields form={createForm} setForm={setCreateForm as React.Dispatch<React.SetStateAction<typeof editForm>>} clients={clients} />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={saving}>{saving ? t('pages.users.createModal.creating') : t('pages.users.createModal.create')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('pages.users.editModal.title')} — {editingUser?.first_name} {editingUser?.family_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-3 pt-1">
            <UserFormFields form={editForm} setForm={setEditForm} clients={clients} />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={saving}>{saving ? t('pages.users.editModal.updating') : t('pages.users.editModal.update')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

